import { useMemo } from 'react';

import {
  CodingStrategyEnum,
  OrientationEnum,
  PaperFormatEnum,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useEditableTrackableData } from '../../../hooks/useEditableTrackableData.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import type { DocumentTemplatePage_OneDocumentTemplate } from '../../DocumentTemplate/hooks/useFindOneDocumentTemplateQuery.hook';

export const useInitialValuesFactory = () => {
  const { generateId } = useIdFactory();
  const { trackableData } = useEditableTrackableData();

  const {
    codingStrategiesCodeMap,
    contentOriginsCodeMap,
    genericStatusesCodeMap,
    documentTypesCodeMap,
    printStylesCodeMap,
  } = useReferencesContext();

  const initialValues: DocumentTemplatePage_OneDocumentTemplate = useMemo(
    () => ({
      __typename: 'DocumentTemplate' as const,
      _id: generateId(),
      codingConfig: {
        __typename: CodingStrategyEnum.CodingConfigManual as const,
        _id: generateId(),
        strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
        code: EMPTY_STRING,
        kind: CodingStrategyEnum.CodingConfigManual,
      },
      code: EMPTY_STRING,
      label: EMPTY_STRING,
      statusId: genericStatusesCodeMap.Enabled._id,
      document: {
        __typename: 'Document' as const,
        _id: generateId(),
        title: EMPTY_STRING,
        virtualRoot: {
          __typename: 'Section' as const,
          _id: generateId(),
          isVirtual: true,
          contentOriginId: contentOriginsCodeMap.Special._id,
          subSections: EMPTY_ARRAY,
          orientation: OrientationEnum.Portrait,
          paperFormat: PaperFormatEnum.A4,
          hasPagebreakAfter: false,
          hasPagebreakBefore: false,
        },
        typeId: documentTypesCodeMap.Report._id,
        kind: documentTypesCodeMap.Report.code,
        printing: {
          __typename: 'DocumentTemplatePrinting' as const,
          _id: generateId(),
          pdfPrints: EMPTY_ARRAY,
          pdfPrintsIncrement: 0,
          printSettings: {
            __typename: 'PrintSettings' as const,
            _id: generateId(),
            addFooters: true,
            addHeaders: true,
            printStyleId: printStylesCodeMap.Blank._id,
          },
        },
        codingConfig: {
          __typename: CodingStrategyEnum.CodingConfigManual as const,
          _id: generateId(),
          strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
          code: EMPTY_STRING,
          kind: CodingStrategyEnum.CodingConfigManual,
        },
        ...trackableData,
      },
      printing: {
        __typename: 'DocumentTemplatePrinting' as const,
        _id: generateId(),
        pdfPrints: EMPTY_ARRAY,
        pdfPrintsIncrement: 0,
        printSettings: {
          __typename: 'PrintSettings' as const,
          _id: generateId(),
          addFooters: true,
          addHeaders: true,
          printStyleId: printStylesCodeMap.Blank._id,
        },
      },
      ...trackableData,
    }),
    [
      codingStrategiesCodeMap.CodingConfigManual._id,
      contentOriginsCodeMap.Special._id,
      documentTypesCodeMap.Report._id,
      documentTypesCodeMap.Report.code,
      generateId,
      genericStatusesCodeMap.Enabled._id,
      printStylesCodeMap.Blank._id,
      trackableData,
    ],
  );

  return { initialValues };
};
